import { useTranslation } from 'react-i18next';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'components/accordion';
import { useReleaseNotes } from 'hooks/useReleaseNotes';
import type { ReleaseNote } from 'types/releaseNotes';

function formatReleaseDate(isoDate: string, locale: string): string {
  const date = new Date(`${isoDate}T00:00:00`);
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

function ReleaseNoteItem({ note, locale }: { note: ReleaseNote; locale: string }) {
  return (
    <AccordionItem value={note.version}>
      <AccordionTrigger className="cursor-pointer hover:no-underline">
        <span className="flex items-center gap-3">
          <span className="font-semibold">v{note.version}</span>
          <span className="text-sm font-normal text-muted-foreground">
            {note.releaseDate != null ? formatReleaseDate(note.releaseDate, locale) : ''}
          </span>
        </span>
      </AccordionTrigger>

      <AccordionContent>
        <ul className="mt-2 space-y-4 pb-1">
          {note.entries.map((entry, index) => (
            <li key={entry.id} className="text-sm leading-relaxed">
              <span className="font-semibold">
                {index + 1}. {entry.title}
              </span>
              {entry.body ? (
                <>
                  {': '}
                  <span className="text-muted-foreground">{entry.body}</span>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      </AccordionContent>
    </AccordionItem>
  );
}

export function ReleaseNotesAccordion() {
  const { t, i18n } = useTranslation();
  const { releaseNotes, isLoading, error } = useReleaseNotes();
  const locale = i18n.language ?? 'de';

  return (
    <div>
      <h2>{t('releaseNotes.title')}</h2>

      {isLoading && <p className="text-sm text-muted-foreground">{t('releaseNotes.loading')}</p>}

      {error && !isLoading && <p className="text-sm text-destructive">{t('releaseNotes.error')}</p>}

      {!isLoading && !error && releaseNotes.length === 0 && (
        <p className="text-sm text-muted-foreground">{t('releaseNotes.noNotes')}</p>
      )}

      {!isLoading && releaseNotes.length > 0 && (
        <Accordion type="single" collapsible className="w-full">
          {releaseNotes.map((note) => (
            <ReleaseNoteItem key={note.id} note={note} locale={locale} />
          ))}
        </Accordion>
      )}
    </div>
  );
}
